import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowRight, AlertCircle, Check } from "lucide-react";
import { MfaStep, BackupCodes } from "@/components/auth/MfaStep";
import { ForgotPassword } from "@/components/auth/ForgotPassword";

const HTSS_LOGO = "https://customer-assets-lqy194kg.emergentagent.net/job_htss-readonly-poc/artifacts/f2cu22pk_htss-logo-2026.png";
const TED_LOGO = "https://customer-assets-lqy194kg.emergentagent.net/job_htss-readonly-poc/artifacts/x951y0yy_logo-wordmark.webp";

const MARQUEE_WORDS = ["Businesses", "Technology", "Efficiency", "Decisions", "Smarter Workflows", "Stronger Businesses"];

export default function Login() {
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [step, setStep] = useState("password");
  const [challenge, setChallenge] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const finish = (data) => {
    completeLogin(data);
    const from = location.state?.from;
    navigate(from && from !== "/login" ? from : "/", { replace: true });
  };

  const afterMfa = (data) => {
    if (data.backup_codes) { setPending(data); setStep("codes"); } else finish(data);
  };

  const backToPassword = () => { setStep("password"); setChallenge(null); setPassword(""); setLoading(false); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.mfa_required) { setChallenge(data); setStep("mfa"); setLoading(false); return; }
      finish(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "w-full h-12 rounded-xl border border-slate-200 bg-white pl-11 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 transition-shadow";

  return (
    <div className="h-screen flex bg-slate-50">
      {/* Left brand hero — TED Ops AI (replicates tedopsai.com) */}
      <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden flex-col justify-between bg-[#060912] text-white" data-testid="login-brand-hero">
        {/* aurora glows */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px circle at 12% 8%, rgba(59,130,246,0.16), transparent 55%), radial-gradient(720px circle at 92% 22%, rgba(139,92,246,0.14), transparent 50%), radial-gradient(1100px circle at 55% 125%, rgba(34,211,238,0.12), transparent 55%)",
          }}
        />
        {/* glowing arc */}
        <svg
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-[38%] w-[170%] h-[85%]"
          viewBox="0 0 800 400"
          fill="none"
          style={{ filter: "drop-shadow(0 0 24px rgba(96,165,250,0.55))" }}
          aria-hidden="true"
        >
          <ellipse cx="400" cy="400" rx="390" ry="372" stroke="url(#tedarc)" strokeWidth="1.6" />
          <defs>
            <linearGradient id="tedarc" x1="0" y1="0" x2="800" y2="0" gradientUnits="userSpaceOnUse">
              <stop stopColor="#22d3ee" stopOpacity="0" />
              <stop offset="0.5" stopColor="#7dd3fc" />
              <stop offset="1" stopColor="#a78bfa" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        {/* wordmark */}
        <div className="relative px-12 pt-10">
          <img src={TED_LOGO} alt="TED Ops AI" className="h-11 w-auto" data-testid="ted-ops-logo" />
          <div className="mt-3 text-[10px] font-medium tracking-[0.35em] text-slate-500 mono">TECHNOLOGY. EFFICIENCY. DECISIONS.</div>
        </div>

        {/* hero headline */}
        <div className="relative px-12">
          <h1 className="font-black leading-[0.98] tracking-tight text-5xl xl:text-6xl">
            <span className="text-white">Smarter </span>
            <span className="bg-gradient-to-r from-sky-400 via-violet-400 to-fuchsia-500 bg-clip-text text-transparent">Workflows.</span>
            <br />
            <span className="text-white">Stronger</span>
            <br />
            <span className="bg-gradient-to-r from-cyan-300 via-sky-400 to-violet-500 bg-clip-text text-transparent">Businesses.</span>
          </h1>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-slate-400">
            AI-powered operations software for real-world businesses.
          </p>
        </div>

        {/* keyword marquee + footer */}
        <div className="relative pb-8">
          <div className="overflow-hidden border-t border-white/5 py-4">
            <div className="flex w-max whitespace-nowrap ted-marquee-track">
              {[0, 1].map((dup) => (
                <div key={dup} className="flex shrink-0 items-center" aria-hidden={dup === 1}>
                  {MARQUEE_WORDS.map((w, i) => (
                    <span key={i} className="flex items-center text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500 mono">
                      {w}
                      <span className="mx-6 text-violet-400/70">◆</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="px-12 pt-3 text-[10px] tracking-[0.3em] text-slate-600 mono">© 2026 TED OPS AI</div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex p-6 sm:p-10 relative overflow-y-auto bg-slate-50">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(700px circle at 85% 15%, rgba(99,102,241,0.10), transparent 55%), radial-gradient(600px circle at 100% 100%, rgba(124,58,237,0.08), transparent 55%)",
          }}
        />
        <div className="relative m-auto w-full max-w-md rounded-3xl bg-white shadow-[0_24px_70px_-20px_rgba(30,41,59,0.35)] ring-1 ring-slate-100 p-8 sm:p-10 tl-fade-up" data-testid="login-card">
          <img src={HTSS_LOGO} alt="The Hot Tub Superstore" className="h-20 w-auto mx-auto mb-9 object-contain" />

          {step === "mfa" && <MfaStep challenge={challenge} onDone={afterMfa} onBack={backToPassword} />}
          {step === "codes" && <BackupCodes codes={pending.backup_codes} onContinue={() => finish(pending)} />}
          {step === "forgot" && <ForgotPassword initialEmail={email} onBack={() => setStep("password")} />}
          {step === "password" && <form onSubmit={submit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-slate-800 mb-1.5">Email address</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                <input id="email" type="email" autoComplete="username" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  data-testid="login-email-input" className={fieldCls} required />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-slate-800 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                <input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                  data-testid="login-password-input" className={`${fieldCls} pr-11`} required />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPw ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setRemember((v) => !v)} className="flex items-center gap-2.5 group" data-testid="login-remember">
                <span className={`flex items-center justify-center w-5 h-5 rounded-md border transition-colors ${remember ? "bg-indigo-600 border-indigo-600" : "bg-white border-slate-300 group-hover:border-indigo-400"}`}>
                  {remember && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className="text-sm text-slate-600">Remember me</span>
              </button>
              <button type="button" onClick={() => { setError(""); setStep("forgot"); }} className="text-sm font-semibold text-indigo-600 hover:text-indigo-700" data-testid="login-forgot">
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/25 transition-all disabled:opacity-60">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Sign in
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>}

          <div className="mt-7 pt-5 border-t border-slate-100 flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-500 shrink-0">
              <ShieldCheck className="w-[18px] h-[18px]" />
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-700">Secure sign-in with authenticator</div>
              <div className="text-xs text-slate-400">Your account is protected with secure sign-in.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
